/**
 * Everything the read plane knows about one player.
 *
 * THE FAN-OUT IS PARALLEL AND EACH VIEW IS INDEPENDENT. One hook per view means one
 * request per view, all in flight together, each with its own ETag and its own failure:
 * if `player_secret_bases` errors, homes and rewards still render and the broken card
 * says so. A single combined query would have made one slow or failing view the whole
 * page's problem, and PostgREST has no join across views to make it worth it.
 *
 * THE CARD LIST IS DATA, NOT MARKUP. Adding `web.player_warp_uses` in a later migration
 * means adding one entry to PLAYER_VIEW_SPECS - no component, no column list, no layout work.
 * Everything below that line is generic; see components/ViewCard.tsx.
 *
 * THE LIST ITSELF LIVES IN ../player/viewSpecs BECAUSE IT IS SHARED with `/me`, which renders the
 * same views about the reader themselves in the second person. One list, two voices; see that file
 * for why a second copy would rot.
 */
import { Link, useParams } from 'react-router';

import { GRANT_MIN_ROLE, roleAtLeast, type PlayerDirectoryRow } from '@dimensionscore/shared';

import { usePolledView, type PolledView } from '../api/usePolledView';
import { useSession } from '../auth/SessionProvider';
import { SignInPanel } from '../components/SessionBar';
import { ViewCard } from '../components/ViewCard';
import { PLAYER_VIEW_SPECS, type InspectorViewName } from '../player/viewSpecs';

/**
 * Every view except the directory, which is this page's header rather than a card, and the server-wide audit
 * surfaces, which are the Audit page's tabs rather than a player's collection.
 */
type PlayerViewName = InspectorViewName;

export function PlayerPage(): React.JSX.Element {
  const { uuid = '' } = useParams();
  const { session, client } = useSession();
  const enabled = session !== null && uuid.length > 0;
  const filter = { player_uuid: `eq.${uuid}` };

  const profile = usePolledView(client, 'player_directory', filter, { enabled });

  /*
   * One hook per view, written out rather than looped: the Rules of Hooks require a
   * fixed call order, and an object literal gives one without an index-into-a-list dance.
   *
   * The type is TOTAL over PlayerViewName on purpose. When a later migration adds a view
   * to WEB_VIEW_NAMES, this record stops compiling until the view is wired up - which is
   * the "one registration" the design asks for, and a great deal better than a new view
   * silently never appearing in the inspector.
   */
  const states: Record<PlayerViewName, PolledView<object>> = {
    player_homes: usePolledView(client, 'player_homes', filter, { enabled }),
    player_pending_rewards: usePolledView(client, 'player_pending_rewards', filter, { enabled }),
    player_redemptions: usePolledView(client, 'player_redemptions', filter, { enabled }),
    player_secret_bases: usePolledView(client, 'player_secret_bases', filter, { enabled }),
    player_base_trusts: usePolledView(client, 'player_base_trusts', filter, { enabled }),
    player_logout_placement: usePolledView(client, 'player_logout_placement', filter, { enabled }),
    player_teleport_prefs: usePolledView(client, 'player_teleport_prefs', filter, { enabled }),
    outbox_status: usePolledView(client, 'outbox_status', { ...filter, order: 'id.desc' }, { enabled }),
    audit_log: usePolledView(client, 'audit_log', { ...filter, order: 'at.desc' }, { enabled }),
  };

  if (session === null) return <SignInPanel />;

  const row = (profile.rows?.[0] ?? null) as PlayerDirectoryRow | null;
  const known = profile.status === 'ready' && row === null;

  return (
    <div data-testid="page-player" data-player-uuid={uuid}>
      <section className="dc-card dc-player-head">
        <p className="dc-dim">
          <Link to="/">&larr; All players</Link>
        </p>
        <h1 data-testid="player-name">{row?.last_known_name ?? (known ? 'Unknown player' : uuid)}</h1>
        <p className="dc-dim">
          <code data-testid="player-uuid">{uuid}</code>
          {row !== null && (
            <>
              {' | '}
              {row.last_seen === null ? 'never seen in game' : `last seen ${new Date(row.last_seen).toLocaleString()}`}
              {' | profile created '}
              {new Date(row.created_at).toLocaleString()}
            </>
          )}
        </p>
        {known && (
          <p className="dc-dim" data-testid="player-unknown">
            No profile row for that UUID. A player who has never joined and has never been granted anything has no profile at all.
          </p>
        )}
        {/*
         * THE SAME CONSTANT THE SIDECAR CHECKS, so "may I draw this" and "may I accept this"
         * cannot drift into disagreement. The link is hidden rather than disabled for a
         * lower tier: the endpoint answers 403 either way, and an editor being shown a door
         * that always slams is worse than not being shown one.
         *
         * A grant needs no profile row to exist first - the writer mints one - so the link
         * is offered for an unknown UUID too, which is precisely the offline-grant case.
         */}
        {session !== null && roleAtLeast(session.claims.role, GRANT_MIN_ROLE) && (
          <p>
            <Link className="dc-linkish" to={`/player/${uuid}/grant`} data-testid="grant-link">
              Grant a reward &rarr;
            </Link>
          </p>
        )}
        {row !== null && row.last_known_name === null && (
          <p className="dc-dim" data-testid="player-never-joined">
            This profile was minted on demand - by an offline grant, most likely - for a player who has never joined, so the server has never
            learned a name for them.
          </p>
        )}
      </section>

      <div className="dc-view-grid">
        {PLAYER_VIEW_SPECS.map((spec) => (
          <ViewCard
            key={spec.view}
            view={spec.view}
            title={spec.title}
            caption={spec.staff.caption}
            emptyText={spec.staff.emptyText}
            state={states[spec.view]}
          />
        ))}
      </div>
    </div>
  );
}
